import { Link } from '@design-system/Link';
import { Image } from '@design-system/Image';
import { Badge } from '@design-system/Badge';
import { cn } from '@/utils/styles';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { Card, CardContent, CardHeader, CardTitle } from '@design-system/Card';
import { useGetSubscriptionStatusQuery } from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { getFormulaImageUrl } from '@/utils/nutrition/getFormulaImageUrl';
import { SMP_PATH , SHOP_PATH} from '@/constants/paths';
import { AccountManagementPageSubscriptionSkeleton } from '@screens/AccountManagementPage/AccountManagementPageSubscriptionSkeleton';

const SubscriptionStatus = {
  Active: 'Active',
  Cancelled: 'Cancelled',
  Paused: 'Paused',
} as const;

const getGradientClassByProduct = (imageUrl: string) => {
  const filename = imageUrl.split('/').pop() || '';
  
  switch (filename) {
    case 'gut_formula.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#EFE6B6_0%,#F0E9E3_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    case 'toothpaste_and_gel.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#CCE3E8_0%,#EEE9E1_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    case 'supplement.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#D5F6D9_0%,#EBEDE0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    case 'oral_lozenges.png':
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#DCF3FF_0%,#E0E2F0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
    default:
      return 'bg-[radial-gradient(70.12%_69.63%_at_71.65%_69.82%,#ccc_0%,#EBEDE0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';
  }
};

export const AccountManagementPageSubscriptionSection = () => {
  const { data, isLoading, isError } = useGetSubscriptionStatusQuery();
  const products = data?.products?.filter(product => product.is_last_subscribed) || [];
  const subscriptionStatus = data?.subscription_details?.subscription_status;
  const productCount = products?.length;

  if (isLoading) {
    return (
      <AccountManagementPageSubscriptionSkeleton />
    )
  }

  if (isError || !data) {
    return;
  }

  const renderProductContent = () => {
    if (productCount === 0) {
      return (
        <div className="flex flex-col gap-2">
          <p className="typography-body text-tertiary">
            You currently don't have any active subscriptions.
          </p>
          <Link
            to={SHOP_PATH}
            className="flex items-center [&_svg]:!size-6 w-full justify-start text-primary hover:text-primary/90"
          >
            Shop our products
            <ChevronRightIcon className="ml-auto size-6 text-tertiary" aria-hidden />
          </Link>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4">
          {productCount >= 3 ? (
            <>
              <div className="flex flex-wrap gap-2">
                {products.slice(0, 4).map((product) => (
                  <div
                    key={product.id}
                    className={cn(
                      "relative size-16 rounded-md overflow-hidden flex items-center justify-center",
                      getGradientClassByProduct(product.image_url)
                    )}
                  >
                    <Image
                      src={getFormulaImageUrl(product.image_url)}
                      alt={product.name}
                      className="object-contain size-10"
                    />
                  </div>
                ))}
              </div>
              <span className="typography-body text-primary">
                {productCount} items
              </span>
            </>
          ) : (
            products.map((product) => (
              <div key={product.id} className="flex items-center gap-4">
                <div
                  className={cn(
                    "relative size-16 rounded-md overflow-hidden flex items-center justify-center",
                    getGradientClassByProduct(product.image_url)
                  )}
                >
                  <Image
                    src={getFormulaImageUrl(product.image_url)}
                    alt={product.name}
                    className="object-contain size-10"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="typography-body text-primary">
                    {product.name}
                  </span>
                  <span className="typography-caption2 text-secondary">
                    30-day supply
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        <Link
          to={SMP_PATH}
          className="flex items-center [&_svg]:!size-6 w-full justify-start text-primary hover:text-primary/90"
        >
          Manage subscription
          <ChevronRightIcon className="ml-auto size-6 text-tertiary" aria-hidden />
        </Link>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <span className="typography-h2 text-primary mb-6">My Viome Plan</span>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-row items-center justify-between typography-h2 text-primary">
            My Subscription
            {subscriptionStatus && (
              <Badge
                size="sm"
                className={cn(
                  "capitalize",
                  subscriptionStatus === SubscriptionStatus.Active && "bg-success text-white",
                  subscriptionStatus === SubscriptionStatus.Cancelled && "bg-tertiary text-white",
                  subscriptionStatus === SubscriptionStatus.Paused && "bg-black text-white"
                )}
              >
                {subscriptionStatus}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent>
          {renderProductContent()}
        </CardContent>
      </Card>
    </div>
  );
};